import { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'

import { countDecimalPlaces } from '../../../../utils/count-decimal-places'

import { floorNumber } from '../../../../utils/floor-number'

import { formatNumber } from '../../../../utils/formatters'

import styles from './styles.module.scss'
import { DashboardPieChartProps } from './dashboard-pie-chart.props'
import { useTranslation } from 'utils/use-translation'

export default function DashboardPieChart({ wallets }: DashboardPieChartProps) {
  const { t } = useTranslation()

  const chartRef = useRef(null)

  const [percents, setPercents] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [walletAmount, setWalletAmount] = useState<number>(0)

  useEffect(() => {
    if (wallets.length !== 0 && walletAmount) {
      calculatePercents()

      setIsLoading(false)
    }
  }, [wallets, walletAmount])

  useEffect(() => {
    if (!isLoading) {
      constructChart(percents)
    }
  }, [isLoading])

  const constructChart = (percents: number[]) => {
    const data = percents
    const colors = ['#D099F9', '#FBE695', '#A6FCDB', '#AEAEAE']
    const width = 170
    const height = 170
    const radius = Math.min(width, height) / 2

    // Проверяем, существует ли уже SVG в элементе
    if (chartRef.current.firstChild) {
      chartRef.current.firstChild.remove()
    }

    const svg = d3
      .select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`)

    const pie = d3.pie().padAngle(0.1) // Добавляем отступы между секторами

    const arc = d3
      .arc()
      .innerRadius(radius - 10) // Устанавливаем внутренний радиус
      .outerRadius(radius) // Устанавливаем внешний радиус
      .cornerRadius(10)

    svg
      .selectAll('path')
      .data(pie(data))
      .enter()
      .append('path')
      .attr(
        'd',
        d3
          .arc()
          .innerRadius(radius - 30)
          .outerRadius(radius)
          .startAngle(0)
          .endAngle(0),
      ) // Начальные значения углов
      .attr('fill', (d, i) => colors[i])
      .transition()
      .duration(1500)
      .attrTween('d', function (d) {
        const interpolate = d3.interpolate(d.startAngle, d.endAngle)
        return function (t) {
          d.endAngle = interpolate(t)
          return arc(d)
        }
      })
  }

  useEffect(() => {
    if (wallets.length) {
      const amount = wallets.reduce((acc, cur) => {
        acc += Number((+cur.balance * +Number(cur.rate)).toFixed(2))
        return acc
      }, 0)
      setWalletAmount(amount)
    }
  }, [wallets])

  const calculatePercents = () => {
    const sortedWallets = [...wallets]
      .filter((item) => +(item.balance * +item.rate).toFixed(2) !== 0.0)
      .sort((a, b) => b.balance * +b.rate - a.balance * +a.rate)

    const totalPercents = sortedWallets.reduce((acc, curr) => (acc += +curr.balance * +curr.rate), 0)

    const currentPercents = []
    for (let i = 0; i < sortedWallets.length; i++) {
      let percent: number = ((sortedWallets[i].balance * +sortedWallets[i].rate) / totalPercents) * 100
      if (countDecimalPlaces(percent) > 2) {
        percent = +percent.toFixed(2)
      }
      if (percent !== 0.0) {
        currentPercents.push(percent)
      }
      if (i === 2) {
        break
      }
    }
    setPercents(currentPercents)

    if (sortedWallets.length > 3) {
      const otherPercent = 100 - currentPercents.reduce((acc, currentValue) => (acc += currentValue))
      const initialPercents = currentPercents
      initialPercents.push(otherPercent)
      setPercents(initialPercents)
    }
  }

  return (
    !isLoading && (
      <div className={styles.chart}>
        <div ref={chartRef}></div>
        <div className={styles.chart__text}>
          <p>{t('wallet_balance')}</p>
          <h5>${countDecimalPlaces(walletAmount) > 2 ? formatNumber(floorNumber(walletAmount, 2)) : formatNumber(walletAmount)}</h5>
        </div>
      </div>
    )
  )
}
